/**
 * Oddit PDP cart bridge.
 *
 * The PDP add-to-cart uses the legacy `<product-form>` web component (bundle.js),
 * which intercepts the submit, shows a spinner, and publishes a `product:form:submit`
 * PubSub event. The only subscriber that actually calls /cart/add lives in the legacy
 * Vuex cart app — which no longer mounts in this theme, so the click just spins forever
 * with no network call.
 *
 * This bridge takes over the submit: it preempts the legacy handler (capture phase +
 * stopImmediatePropagation) and routes the add through the Oddit cart drawer
 * (window.OdditCartDrawer), which performs the /cart/add.js request and re-renders
 * itself from /cart.js. No dependency on the legacy bundle.
 */
(function () {
  'use strict';

  if (window.__odditPdpCartBound) return;
  window.__odditPdpCartBound = true;

  var FORM_SELECTOR = 'form[data-type="add-to-cart-form"]';

  function getDrawer() {
    return window.OdditCartDrawer || null;
  }

  function setLoading(form, loading) {
    var btn = form.querySelector('[type="submit"][name="add"]') || form.querySelector('[type="submit"]');
    if (btn) {
      if (loading) {
        btn.setAttribute('aria-disabled', 'true');
        btn.classList.add('loading');
      } else {
        btn.removeAttribute('aria-disabled');
        btn.classList.remove('loading');
      }
    }
    var spinner = form.querySelector('.loading-overlay__spinner');
    if (spinner) spinner.classList.toggle('hidden', !loading);
  }

  function showError(form, message) {
    var wrap = form.querySelector('.product-form__error-message-wrapper');
    var el = wrap ? wrap.querySelector('.product-form__error-message') : null;
    if (wrap) wrap.toggleAttribute('hidden', !message);
    if (el) el.textContent = message || '';
  }

  // Flatten the form (including controls associated via the `form=` attribute, e.g.
  // Skio's selling_plan and properties[*]) into a single add-to-cart line item.
  function parseForm(form) {
    var item = { id: null, quantity: 1, properties: {} };
    var fd = new FormData(form);
    fd.forEach(function (value, key) {
      if (key === 'id') {
        item.id = value;
      } else if (key === 'quantity') {
        item.quantity = Number(value) || 1;
      } else if (key === 'selling_plan') {
        if (value) item.selling_plan = value;
      } else {
        var m = key.match(/^properties\[(.+)\]$/);
        if (m && value !== '') item.properties[m[1]] = value;
      }
    });
    if (Object.keys(item.properties).length === 0) delete item.properties;
    return item;
  }

  function onSubmit(event) {
    var form = event.target;
    if (!form || typeof form.matches !== 'function' || !form.matches(FORM_SELECTOR)) return;

    // Don't hijack the dynamic checkout / Shop Pay button — only the add button.
    var submitter = event.submitter;
    if (submitter && submitter.getAttribute('name') !== 'add') return;

    var drawer = getDrawer();
    // If the Oddit drawer isn't available, leave the event alone so the browser's
    // native form POST to /cart/add can act as a graceful fallback.
    if (!drawer || typeof drawer.addMultipleToCart !== 'function') return;

    // Preempt the legacy <product-form> target-phase submit handler.
    event.preventDefault();
    event.stopImmediatePropagation();

    var item = parseForm(form);
    if (!item.id) return;

    showError(form, '');
    setLoading(form, true);

    drawer.addMultipleToCart([item])
      .then(function () {
        if (typeof drawer.open === 'function') drawer.open();
      })
      .catch(function (err) {
        showError(form, (err && err.message) || 'Sorry, this item could not be added to your cart.');
        if (window.console && console.error) console.error('[oddit-pdp-cart] add failed:', err);
      })
      .then(function () {
        setLoading(form, false);
      });
  }

  // Capture phase so this runs before the legacy form-level submit listener.
  document.addEventListener('submit', onSubmit, true);
})();
